import { Link } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { cn } from "~/lib/utils";
import {
  AUTOMATION_TEMPLATES,
  type AutomationTemplateCategoryFilter,
  type AutomationTemplateDefinition,
} from "./automationTemplates";
import type { AutomationDraft, AutomationProjectOption } from "./automationFormModel";
import { blankAutomationDraft, draftFromTemplate } from "./automationFormModel";
import { CreateAutomationEditor } from "./CreateAutomationEditor";
import { usePublishBreadcrumbLabel } from "~/components/breadcrumb/globalBreadcrumbStore";
import { AutomationFormHeader } from "./AutomationFormHeader";
import { AutomationTemplatePane } from "./AutomationTemplatePane";
import { useAutomationProviderSelection } from "./useAutomationProviderSelection";
import { useAutomationFormSubmission } from "./useAutomationFormSubmission";
import type { AutomationSubmissionResult } from "./useAutomationFormSubmission";

interface AutomationFormLayoutProps {
  readonly busy: boolean;
  readonly error: string | null;
  readonly everyMinuteAvailable: boolean;
  readonly initialDraft: AutomationDraft;
  readonly localExecutionAvailable: boolean;
  readonly mode: "create" | "edit";
  readonly projects: readonly AutomationProjectOption[];
  readonly targetLocked: boolean;
  readonly onSubmit: (draft: AutomationDraft) => Promise<AutomationSubmissionResult>;
}

interface SetupPaneProps {
  readonly draft: AutomationDraft;
  readonly error: string | null;
  readonly everyMinuteAvailable: boolean;
  readonly localExecutionAvailable: boolean;
  readonly mode: AutomationFormLayoutProps["mode"];
  readonly mobileStep: MobileStep;
  readonly projects: readonly AutomationProjectOption[];
  readonly selectedTemplate: AutomationTemplateDefinition | null;
  readonly targetLocked: boolean;
  readonly providerSelection: ReturnType<typeof useAutomationProviderSelection>;
  readonly selectionValid: boolean;
  readonly onDraftChange: (draft: AutomationDraft) => void;
  readonly onShowTemplates: () => void;
}

function providerStatusText(props: SetupPaneProps): string {
  if (
    props.providerSelection.access.status === "loading" ||
    props.providerSelection.inventoryLoading
  ) {
    return "Checking available models…";
  }
  if (props.providerSelection.inventoryError !== null) {
    return "Model availability could not be refreshed. Check your connection and try again.";
  }
  if (props.providerSelection.access.status === "error") {
    return "Provider access could not be verified. Connect an allowed provider in Settings and try again.";
  }
  if (props.providerSelection.projectInventoryStatus === "ambiguous") {
    return "Multiple cloud Environments were found for this project. Resolve the conflict before continuing.";
  }
  if (props.providerSelection.projectInventoryStatus !== "ready") {
    return "This project has not published a model catalog yet. Start or reconnect its cloud runtime, then retry.";
  }
  if (props.selectionValid) {
    return "This exact provider and model will be revalidated before every run.";
  }
  return props.draft.modelSelection
    ? "This saved provider or model is no longer available. Choose another to continue."
    : "No authorized executable model is available. Connect an allowed provider in Settings or refresh the project's provider inventory.";
}

function automationProviderPickerProps(props: SetupPaneProps) {
  const resolvedSelection = props.draft.modelSelection ?? props.providerSelection.selection;
  if (props.draft.runLocation !== "cloud" || resolvedSelection === null) return null;
  return {
    // The picker's own input: the project's Provider Inventory as published,
    // not the eligible subset. `providerAvailability` is what tells the
    // picker which of these a person may not select, and why.
    providers: props.providerSelection.providers,
    providerAvailability: props.providerSelection.providerAvailability,
    provider: resolvedSelection.provider,
    model: resolvedSelection.model,
    modelOptionsByProvider: props.providerSelection.modelOptionsByProvider,
    orgProviderPolicy: props.providerSelection.orgProviderPolicy,
    statusText: providerStatusText(props),
    onChange: (provider: typeof resolvedSelection.provider, model: string) =>
      props.onDraftChange({ ...props.draft, modelSelection: { provider, model } }),
  };
}

function SetupPane(props: SetupPaneProps) {
  const hideOnMobile = props.mode === "create" && props.mobileStep === "templates";
  const providerSelection = automationProviderPickerProps(props);

  return (
    <section
      aria-label="Automation setup"
      className={cn(
        "min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain",
        hideOnMobile ? "max-[900px]:hidden" : null,
      )}
    >
      <div className="mx-auto w-full max-w-2xl px-4 py-7 sm:px-6 md:py-10">
        {props.mode === "create" ? (
          <Button
            className={cn(RESPONSIVE_TEXT_BUTTON_CLASS, "mb-6 hidden max-[900px]:inline-flex")}
            size="sm"
            variant="ghost"
            onClick={props.onShowTemplates}
          >
            <ArrowLeftIcon />
            Templates
          </Button>
        ) : null}
        {props.selectedTemplate ? (
          <div className="mb-8 border-b border-border/60 pb-5">
            <p className="text-xs font-medium text-muted-foreground">
              {props.selectedTemplate.category} template
            </p>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              {props.selectedTemplate.description}
            </p>
          </div>
        ) : null}
        <CreateAutomationEditor
          draft={props.draft}
          everyMinuteAvailable={props.everyMinuteAvailable}
          localExecutionAvailable={
            props.localExecutionAvailable || props.draft.runLocation === "local"
          }
          projects={props.projects}
          runLocationLocked={props.mode === "edit"}
          targetLocked={props.targetLocked}
          template={props.selectedTemplate}
          providerSelection={providerSelection}
          providerStatusText={providerStatusText(props)}
          onDraftChange={props.onDraftChange}
        />
        {props.error ? <p className="mt-6 text-sm text-destructive">{props.error}</p> : null}
      </div>
    </section>
  );
}

interface FormFooterProps {
  readonly busy: boolean;
  readonly canSubmit: boolean;
  readonly mode: AutomationFormLayoutProps["mode"];
  readonly mobileStep: MobileStep;
  readonly onSubmit: () => void;
}

function RecoveryActions({ automationId }: { readonly automationId: string }) {
  return (
    <div className="flex items-center justify-end gap-2 border-t border-border/60 px-4 py-3 sm:px-6">
      <Button render={<Link to="/automations" />} variant="ghost">
        Back to Automations
      </Button>
      <Button render={<Link params={{ automationId }} to="/automations/$automationId" />}>
        Open automation
      </Button>
    </div>
  );
}

function FormFooter({ busy, canSubmit, mode, mobileStep, onSubmit }: FormFooterProps) {
  const hideOnMobile = mode === "create" && mobileStep === "templates";
  const submitLabel = busy ? "Saving…" : mode === "create" ? "Create automation" : "Save changes";
  return (
    <footer
      className={cn(
        "flex shrink-0 items-center justify-end gap-2 border-t border-border/60 bg-background px-4 py-3 sm:px-6",
        hideOnMobile ? "max-[900px]:hidden" : null,
      )}
    >
      <Button
        className={RESPONSIVE_TEXT_BUTTON_CLASS}
        render={<Link to="/automations" />}
        variant="ghost"
      >
        Cancel
      </Button>
      <Button className={RESPONSIVE_TEXT_BUTTON_CLASS} disabled={!canSubmit} onClick={onSubmit}>
        {submitLabel}
      </Button>
    </footer>
  );
}

type MobileStep = "templates" | "setup";

export function canSubmitDraft(
  draft: AutomationDraft,
  busy: boolean,
  modelSelectionValid: boolean,
): boolean {
  const hasTarget = draft.runLocation === "local" || draft.selectedProjectId.length > 0;
  const hasExecutableModel =
    draft.runLocation === "local" || (draft.modelSelection !== null && modelSelectionValid);
  return (
    !busy &&
    draft.name.trim().length > 0 &&
    draft.prompt.trim().length > 0 &&
    hasTarget &&
    hasExecutableModel
  );
}

export function AutomationFormLayout(props: AutomationFormLayoutProps) {
  const initialTemplate = props.mode === "create" ? (AUTOMATION_TEMPLATES[0] ?? null) : null;
  const [selectedTemplate, setSelectedTemplate] = useState<AutomationTemplateDefinition | null>(
    initialTemplate,
  );
  const [draft, setDraft] = useState<AutomationDraft>(() =>
    initialTemplate ? draftFromTemplate(initialTemplate, props.projects) : props.initialDraft,
  );
  const [category, setCategory] = useState<AutomationTemplateCategoryFilter>("All");
  const [query, setQuery] = useState("");
  const [mobileStep, setMobileStep] = useState<MobileStep>(
    props.mode === "create" ? "templates" : "setup",
  );
  const providerSelection = useAutomationProviderSelection({ draft });
  const effectiveSelection =
    props.mode === "create"
      ? (draft.modelSelection ?? providerSelection.selection)
      : draft.modelSelection;
  const effectiveDraft = { ...draft, modelSelection: effectiveSelection };
  const selectionValid =
    effectiveSelection !== null &&
    providerSelection.access.status === "ready" &&
    !providerSelection.inventoryLoading &&
    providerSelection.inventoryError === null &&
    providerSelection.isSelectionValid(effectiveSelection);
  const canSubmit = canSubmitDraft(effectiveDraft, props.busy, selectionValid);
  const submission = useAutomationFormSubmission({
    draft: effectiveDraft,
    canSubmit,
    onSubmit: props.onSubmit,
  });

  function updateDraft(next: AutomationDraft): void {
    submission.clearError();
    setDraft(next);
  }

  function selectTemplate(template: AutomationTemplateDefinition) {
    submission.clearError();
    setSelectedTemplate(template);
    setDraft(draftFromTemplate(template, props.projects));
    setMobileStep("setup");
  }

  function selectBlank() {
    submission.clearError();
    setSelectedTemplate(null);
    setDraft(blankAutomationDraft(props.projects));
    setMobileStep("setup");
  }

  // `/automations/:id/edit` renders `Automations / <name> / Edit`. The middle
  // crumb is the automation, and only this form knows its name — the detail
  // page that normally publishes it is not mounted here.
  usePublishBreadcrumbLabel("automation-detail", props.mode === "edit" ? draft.name : null);

  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background text-foreground">
      <AutomationFormHeader mode={props.mode} />
      <div className="flex min-h-0 flex-1">
        {props.mode === "create" ? (
          <AutomationTemplatePane
            category={category}
            mobileVisible={mobileStep === "templates"}
            query={query}
            selectedTemplate={selectedTemplate}
            onBlank={selectBlank}
            onCategoryChange={setCategory}
            onQueryChange={setQuery}
            onSelect={selectTemplate}
          />
        ) : null}
        <SetupPane
          draft={effectiveDraft}
          error={submission.error ?? props.error}
          everyMinuteAvailable={props.everyMinuteAvailable}
          localExecutionAvailable={props.localExecutionAvailable}
          mode={props.mode}
          mobileStep={mobileStep}
          projects={props.projects}
          providerSelection={providerSelection}
          selectionValid={selectionValid}
          selectedTemplate={selectedTemplate}
          targetLocked={props.targetLocked}
          onDraftChange={updateDraft}
          onShowTemplates={() => setMobileStep("templates")}
        />
      </div>
      <FormFooter
        busy={props.busy || submission.submitting}
        canSubmit={canSubmit && !submission.submitting && !submission.reconciliationRequired}
        mode={props.mode}
        mobileStep={mobileStep}
        onSubmit={() => void submission.submit()}
      />
      {submission.recoveryAutomationId ? (
        <RecoveryActions automationId={submission.recoveryAutomationId} />
      ) : null}
    </main>
  );
}
